'use client'

import { CalendarIcon, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, useRef, type FC, type ReactNode } from 'react'

import { Calendar } from './Calendar'
import { Button, buttonVariants, Popover, PopoverContent, PopoverTrigger, ScrollArea } from '@fp/ui-kit'
import { cn } from '@lib/cn'
import { dayjs } from '@lib/dayjs'
import { iconStroke } from '@lib/iconProps'
import { backendIsoToPickerDate, pickerDateToBackendIso } from '@lib/tz'

type DateSinglePickerProps = {
  value?: string | null
  onChange?: (iso?: string | null) => void
  onSuccess?: (iso?: string | null) => void
  className?: string
  triggerClassName?: string
  disableFutureDates?: boolean
  disablePastDates?: boolean
  isFullWidth?: boolean
  showTime?: boolean
  disabled?: boolean
  /** Custom popover trigger. When set, replaces the default date-display button (and its clear control). */
  trigger?: ReactNode
}

const toPickerDate = (iso?: string | null): Date | undefined => (iso ? backendIsoToPickerDate(iso) : undefined)

const fromPickerDate = (date?: Date): string | undefined => (date ? pickerDateToBackendIso(date) : undefined)

const TimeScroll: FC<{
  value: string
  onChange: (value: string) => void
  max: number
  label: string
}> = ({ value, onChange, max, label }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    const selectedIndex = parseInt(value)
    if (scrollRef.current && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      })
    }
  }, [value])

  const items = Array.from({ length: max + 1 }, (_, i) => i.toString().padStart(2, '0'))

  return (
    <div className='flex flex-col'>
      <div className='text-xs text-muted-foreground m-2 text-center font-medium'>{label}</div>
      <ScrollArea ref={scrollRef} className='h-[300px]'>
        <div className='flex flex-col'>
          {items.map((item, index) => (
            <Button
              key={item}
              variant={value === item ? 'primary' : 'ghost'}
              ref={(el) => {
                itemRefs.current[index] = el
              }}
              onClick={() => onChange(item)}
              className='mr-3'
            >
              {item}
            </Button>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

export const DateSinglePicker: FC<DateSinglePickerProps> = ({
  value,
  onChange,
  onSuccess,
  className,
  triggerClassName,
  disableFutureDates = true,
  disablePastDates = false,
  isFullWidth = false,
  showTime = true,
  disabled = false,
  trigger,
}) => {
  const initialDate = useMemo(() => toPickerDate(value), [value])
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState<Date | undefined>(initialDate)
  const [hour, setHour] = useState(() => (initialDate ? dayjs(initialDate).format('HH') : '00'))
  const [minute, setMinute] = useState(() => (initialDate ? dayjs(initialDate).format('mm') : '00'))

  useEffect(() => {
    const next = toPickerDate(value)
    setDate(next)
    if (next) {
      const d = dayjs(next)
      setHour(d.format('HH'))
      setMinute(d.format('mm'))
    }
  }, [value])

  const handleCalendarSelect = useCallback(
    (selected: Date | undefined) => {
      if (showTime) {
        const updatedDate = dayjs(selected).hour(parseInt(hour)).minute(parseInt(minute)).toDate()
        setDate(updatedDate)
        if (!onSuccess) {
          onChange?.(fromPickerDate(updatedDate))
        }
      } else {
        setDate(selected)
        if (!onSuccess) {
          onChange?.(fromPickerDate(selected))
        }
      }
    },
    [onChange, onSuccess, showTime, hour, minute],
  )

  const handleTimeChange = useCallback(
    (newHour: string, newMinute: string) => {
      if (date) {
        const updatedDate = dayjs(date).hour(parseInt(newHour)).minute(parseInt(newMinute)).toDate()
        setDate(updatedDate)
        onChange?.(fromPickerDate(updatedDate))
      }
    },
    [date, onChange],
  )

  const handleHourChange = useCallback(
    (newHour: string) => {
      setHour(newHour)
      handleTimeChange(newHour, minute)
    },
    [minute, handleTimeChange],
  )

  const handleMinuteChange = useCallback(
    (newMinute: string) => {
      setMinute(newMinute)
      handleTimeChange(hour, newMinute)
    },
    [hour, handleTimeChange],
  )

  const handleApply = useCallback(() => {
    onSuccess?.(fromPickerDate(date))
    setOpen(false)
  }, [onSuccess, date])

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setDate(undefined)
      setHour('00')
      setMinute('00')
      onChange?.(undefined)
    },
    [onChange],
  )

  const formatDisplayValue = () => {
    if (!date) return 'Select date'

    return showTime ? dayjs(date).format('YYYY-MM-DD HH:mm') : dayjs(date).format('YYYY-MM-DD')
  }

  return (
    <div className={cn('flex gap-2 items-center relative', className)}>
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          {trigger ?? (
            <button
              type='button'
              disabled={disabled}
              className={cn(
                buttonVariants({ variant: 'outline' }),
                'justify-start text-left font-normal border-input min-h-9.5 group px-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:border-input/50 disabled:hover:bg-card',
                isFullWidth ? 'w-full' : 'w-[260px]',
                !date && 'text-muted-foreground',
                triggerClassName,
              )}
            >
              <div className='flex justify-start items-center w-full'>
                <CalendarIcon className='mr-2 h-4 w-4 text-muted-foreground' strokeWidth={iconStroke.bold} />
                <div className={date ? 'text-foreground' : 'text-muted-foreground'}>{formatDisplayValue()}</div>
              </div>
            </button>
          )}
        </PopoverTrigger>
        {!trigger && date && !disabled && (
          <Button
            onClick={handleClear}
            variant='ghost'
            size='icon'
            type='button'
            className='absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground size-5!'
          >
            <X className='size-4!' />
          </Button>
        )}

        <PopoverContent className='w-auto p-0' align='start' forceMount asChild>
          <div className='flex'>
            <div className='p-4'>
              <Calendar
                mode='single'
                numberOfMonths={2}
                selected={date}
                onDayClick={handleCalendarSelect}
                onSelect={handleCalendarSelect}
                defaultMonth={new Date()}
                disableFutureDates={disableFutureDates}
                disablePastDates={disablePastDates}
              />

              {onSuccess && (
                <div className='mt-3 flex gap-2'>
                  <Button onClick={handleApply} className='flex-1'>
                    Apply
                  </Button>
                </div>
              )}
            </div>

            {showTime && (
              <>
                <div className='w-px bg-border' />
                <div className='p-4 flex gap-2'>
                  <TimeScroll value={hour} onChange={handleHourChange} max={23} label='Hour' />
                  <TimeScroll value={minute} onChange={handleMinuteChange} max={59} label='Min' />
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
